import React, { useState, useEffect } from 'react'
import { Book, ExternalLink, RefreshCw } from 'lucide-react'
import AdminLayout from '../../components/Admin/AdminLayout'
import { Card } from '../../components/ui/card'
import { Button } from '../../components/ui/button'

const ApiDocsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [swaggerUrl, setSwaggerUrl] = useState<string | null>(null)

  useEffect(() => {
    checkDocumentationAvailability()
  }, [])

  const checkDocumentationAvailability = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      // Check if documentation is available
      const response = await fetch('/api/docs/openapi.json')
      if (response.ok) {
        setSwaggerUrl('/api/docs/swagger')
      } else {
        setError('API documentation is not available in this environment')
      }
    } catch (err) {
      setError('Failed to load API documentation')
    } finally {
      setIsLoading(false)
    }
  }

  const refreshDocs = () => {
    checkDocumentationAvailability()
  }

  if (isLoading) {
    return (
      <AdminLayout title="API Documentation">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-martinique/60" />
            <p className="text-martinique/70 dark:text-white/70">Loading API documentation...</p>
          </div>
        </div>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout title="API Documentation">
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-semibold text-martinique dark:text-white">API Documentation</h2>
              <p className="text-martinique/70 dark:text-white/70 mt-1">Interactive API documentation and testing</p>
            </div>
            <Button onClick={refreshDocs} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>

          <Card className="p-8 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card">
            <div className="text-center">
              <Book className="h-12 w-12 mx-auto mb-4 text-martinique/40 dark:text-white/40" />
              <h3 className="text-lg font-medium text-martinique dark:text-white mb-2">Documentation Unavailable</h3>
              <p className="text-martinique/70 dark:text-white/70 mb-4">{error}</p>
              <p className="text-sm text-martinique/60 dark:text-white/60">
                API documentation is only available in development and staging environments.
              </p>
            </div>
          </Card>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout title="API Documentation">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold text-martinique dark:text-white">API Documentation</h2>
            <p className="text-martinique/70 dark:text-white/70 mt-1">Interactive API documentation and testing</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={refreshDocs} variant="outline">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            {swaggerUrl && (
              <Button 
                onClick={() => window.open(swaggerUrl, '_blank')} 
                variant="outline"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open in New Tab
              </Button>
            )}
          </div>
        </div>

        {/* Documentation Links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card hover:shadow-cardHover transition-all duration-200">
            <div className="flex items-center mb-4">
              <Book className="h-6 w-6 mr-3 text-coral dark:text-darkspice" />
              <h3 className="text-lg font-medium text-martinique dark:text-white">Health Monitoring API</h3>
            </div>
            <p className="text-martinique/70 dark:text-white/70 mb-4">
              Comprehensive health check endpoints for monitoring system components and external dependencies.
            </p>
            <div className="text-sm text-martinique/60 dark:text-white/60">
              • Database connectivity checks<br />
              • External service monitoring<br />
              • Email service validation<br />
              • Application health status
            </div>
          </Card>

          <Card className="p-6 bg-sand dark:bg-darkcard border-0 rounded-xl shadow-card hover:shadow-cardHover transition-all duration-200">
            <div className="flex items-center mb-4">
              <ExternalLink className="h-6 w-6 mr-3 text-coral dark:text-darkspice" />
              <h3 className="text-lg font-medium text-martinique dark:text-white">Development Tools</h3>
            </div>
            <p className="text-martinique/70 dark:text-white/70 mb-4">
              Development-only endpoints for API introspection and system debugging.
            </p>
            <div className="text-sm text-martinique/60 dark:text-white/60">
              • Endpoint discovery<br />
              • Schema introspection<br />
              • Development utilities<br />
              • Debug information
            </div>
          </Card>
        </div>

        {/* Embedded Swagger UI */}
        {swaggerUrl && (
          <Card className="p-0 bg-white dark:bg-darkcard border-0 rounded-xl shadow-card overflow-hidden">
            <div className="bg-sand dark:bg-martinique px-6 py-4 border-b border-martinique/10 dark:border-white/10">
              <h3 className="text-lg font-medium text-martinique dark:text-white">Interactive API Explorer</h3>
              <p className="text-sm text-martinique/70 dark:text-white/70 mt-1">
                Test API endpoints directly in your browser
              </p>
            </div>
            <div className="min-h-[600px]">
              <iframe
                src={swaggerUrl}
                width="100%"
                height="600"
                frameBorder="0"
                title="API Documentation"
                className="w-full"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </div>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}

export default ApiDocsPage